import { Map, AdvancedMarker } from '@vis.gl/react-google-maps'
import { useTranslation } from 'react-i18next'

export interface ApartmentMapPreviewProps {
  /**
   * Geographic latitude of the pin.
   */
  lat: number
  /**
   * Geographic longitude of the pin.
   */
  lng: number
  /**
   * Optional accessible label for the pin element.
   * Defaults to t('forms:mapMarkerLabel').
   */
  markerLabel?: string
}

/**
 * Read-only map panel showing a single pin at the provided coordinates.
 * Allows the apartment owner to visually verify the geocoded location.
 *
 * Does not render when both lat and lng are exactly 0 (null island guard).
 * The APIProvider must be present higher in the tree (e.g. in ApartmentForm).
 */
export function ApartmentMapPreview({ lat, lng, markerLabel }: ApartmentMapPreviewProps) {
  const { t } = useTranslation(['forms'])

  // Null island guard — lat=0 and lng=0 exactly is almost certainly missing data
  if (lat === 0 && lng === 0) return null

  const center = { lat, lng }
  const label = markerLabel ?? t('forms:mapMarkerLabel')

  return (
    <div
      className="w-full rounded-lg overflow-hidden border border-gray-200"
      style={{ height: '300px' }}
      aria-label={t('forms:verifyLocation')}
    >
      <Map
        center={center}
        zoom={15}
        mapId={import.meta.env.VITE_GOOGLE_MAP_ID ?? 'DEMO_MAP_ID'}
        gestureHandling="greedy"
        disableDefaultUI
        style={{ width: '100%', height: '100%' }}
      >
        <AdvancedMarker position={center} title={label} />
      </Map>
    </div>
  )
}
